import ExcelJS from 'exceljs'
import { ReturnManagementFlagChoice } from '@/services/return/returnManagement/types'
import { EXCEL_COLUMNS, IMPORTABLE_KEYS } from './columnDefinitions'

const titleToKey = new Map(EXCEL_COLUMNS.map(({ title, key }) => [title, key]))
const importable = new Set(IMPORTABLE_KEYS)

/** `#REF!`·`#DIV/0!` 등 오류 셀 판별. `String()` 으로 떨어뜨리면 `"[object Object]"` 가 값이 된다. */
const isErrorValue = (value: unknown) => typeof value === 'object' && value !== null && 'error' in value

/** 날짜형 셀 값을 화면 행의 `plnDate` 계약(`YYYY-MM-DD`, types.ts:196)과 맞춘다. */
const dateCellText = (date: Date): string => date.toISOString().slice(0, 10)

/** 수식 결과값(`CellFormulaValue.result`)을 문자열로 좁힌다. */
const formulaResultText = (result: ExcelJS.CellFormulaValue['result']): string => {
  if (isErrorValue(result)) return ''
  return result instanceof Date ? dateCellText(result) : String(result ?? '')
}

/**
 * 셀 값을 문자열로 읽는다. ExcelJS `cell.value` 는 날짜·하이퍼링크·서식 텍스트·수식을 객체로 준다
 * (outboundEtc/excelIO.ts 의 같은 함수와 동일 사정 — 화면 폴더 안 사본, D5).
 *
 * 날짜형 셀은 `yyyyMMdd` 가 아니라 `YYYY-MM-DD` 로 돌려준다 — `plnDate` 만 datepicker 변형(importable)
 * 이라 실질적으로 영향받는 건 반품일자뿐인데, 그 필드의 화면 행 계약이 대시 포맷이다(리뷰 반영 —
 * 이전엔 `yyyyMMdd` 로 내려줘 저장 시 `toCompactDate` 가 형식 불일치로 빈 문자열을 만들어 400이 났다).
 */
const cellText = (raw: ExcelJS.CellValue): string => {
  if (raw == null) return ''
  if (raw instanceof Date) return dateCellText(raw)
  if (typeof raw !== 'object') return String(raw)
  if (isErrorValue(raw)) return ''
  if ('richText' in raw) return raw.richText.map((run) => run.text).join('')
  if ('formula' in raw || 'sharedFormula' in raw) return formulaResultText((raw as ExcelJS.CellFormulaValue).result)
  if ('text' in raw) return cellText((raw as { text: ExcelJS.CellValue }).text)
  return String(raw)
}

/**
 * 검색(FLAG) 칸 — 대소문자만 다른 값은 같은 값으로 보고, 그 밖의 값은 파일을 물린다(리뷰 반영).
 *
 * 저장 계약이 boolean 이라 알 수 없는 값은 `toFlagBoolean` 이 미전송으로 떨어뜨리고, BE 는 미전송을
 * create=false 로 다룬다 — 사용자가 적어 넣은 값이 안내 없이 N 으로 저장된다. 서버 다운로드 양식
 * (moms-be `ReturnManagementExcelDto`, 15컬럼)에는 검색 칸이 없어 이 칸은 사람이 직접 적은 것이다.
 */
const toFlagCellText = (text: string, rowNumber: number): ReturnManagementFlagChoice => {
  const upper = text.toUpperCase()
  if (upper === 'Y' || upper === 'N') return upper
  throw new Error(`검색 칸은 Y 또는 N 만 넣을 수 있습니다(${rowNumber}행: "${text}").`)
}

/**
 * 엑셀 업로드 파싱 — 계획서 5장 9단계. 양식은 다운로드 결과(EXCEL_COLUMNS)를 그대로 쓴다.
 * 편집 셀(IMPORTABLE_KEYS)만 받는다 — 표시 전용·파생 컬럼은 저장 계약에 없거나 서버가 채운다.
 */
export const parseReturnManagementExcel = async (file: File): Promise<Record<string, unknown>[]> => {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(await file.arrayBuffer())
  const sheet = workbook.worksheets[0]
  if (!sheet) return []

  const headerKeys: (string | null)[] = []
  sheet.getRow(1).eachCell({ includeEmpty: true }, (cell, col) => {
    const key = titleToKey.get(cellText(cell.value).trim()) ?? null
    headerKeys[col] = key && importable.has(key) ? key : null
  })
  if (!headerKeys.some(Boolean)) {
    throw new Error('헤더를 인식할 수 없습니다. 엑셀 다운로드 양식과 동일한 1행 헤더가 필요합니다.')
  }

  const rows: Record<string, unknown>[] = []
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return

    const item: Record<string, unknown> = {}
    let hasValue = false
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const key = headerKeys[col]
      if (!key) return
      const text = cellText(cell.value).trim()
      if (!text) return
      hasValue = true
      if (key === 'ordQty') item[key] = Number(text) || 0
      else if (key === 'flag') item[key] = toFlagCellText(text, rowNumber)
      else item[key] = text
    })

    if (hasValue) rows.push(item)
  })

  return rows
}
